import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { formatApiError } from "../api";
import {
  Truck, SignIn, Eye, EyeSlash, MapTrifold, IdentificationBadge, ClipboardText,
} from "@phosphor-icons/react";

// Bundled in /app/frontend/public/login-hero.png — served same-origin so the asset
// is part of the build and cannot be corrupted by an upload pipeline or CDN.
const HERO_SRC = `${process.env.PUBLIC_URL || ""}/login-hero.png`;
const COPY_YEAR = new Date().getFullYear();

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [heroFailed, setHeroFailed] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const data = await login(email, password);
      nav(data.role === "driver" ? "/driver" : "/admin", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen relative bg-black overflow-hidden" data-testid="login-page">
      {/* HERO — centered, fully-visible dumpster with the studio spotlight intact. */}
      <img
        src={HERO_SRC}
        alt="ZMEX Disposal commercial dumpster on dark studio backdrop"
        onLoad={() => setHeroLoaded(true)}
        onError={() => setHeroFailed(true)}
        draggable={false}
        loading="eager"
        decoding="async"
        fetchpriority="high"
        data-testid="login-hero-image"
        className={`absolute inset-0 w-full h-full object-contain object-center select-none pointer-events-none transition-opacity duration-700 ${heroLoaded ? "opacity-100" : "opacity-0"}`}
      />
      {heroFailed && (
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-black to-black" />
      )}

      {/* Side fades so the left copy & right card sit on near-pure black while the
          center dumpster keeps its full studio spotlight. */}
      <div className="absolute inset-y-0 left-0 w-[34%] bg-gradient-to-r from-black via-black/80 to-transparent pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-[34%] bg-gradient-to-l from-black via-black/80 to-transparent pointer-events-none" />

      {/* RESPONSIVE TOP HEADER —
            • ≥640px: logo lockup left, status badge right (matches reference)
            • iPhone (< 640px): logo + badge stack vertically and CENTER so the badge
              never overlaps the brand mark
          Safe-area inset on top so iPhone Safari's notch/URL bar doesn't crash into the brand. */}
      <header
        className="absolute top-0 inset-x-0 z-20 px-4 sm:px-10 flex flex-col sm:flex-row items-center sm:justify-between gap-3 sm:gap-4"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)", paddingBottom: "10px" }}
      >
        {/* LOGO LOCKUP */}
        <div className="flex items-center gap-3">
          <div className="relative w-11 h-11 sm:w-12 sm:h-12 flex-shrink-0">
            <span
              aria-hidden
              className="absolute inset-0 rounded-sm"
              style={{
                background: "conic-gradient(from 0deg, transparent 0deg, rgba(207,171,122,0.65) 65deg, transparent 110deg, transparent 360deg)",
                filter: "blur(0.3px)",
                animation: "zmexRingSpin 2s linear 1 forwards, zmexRingIdle 14s linear 2s infinite",
              }}
            />
            <div className="absolute inset-[2px] rounded-sm border border-white/15 bg-black grid place-items-center text-[#CFAB7A]">
              <Truck size={20} weight="bold" />
            </div>
          </div>
          <div className="leading-none">
            <div className="font-heading font-black text-white tracking-[0.18em] text-[15px] sm:text-base">ZMEX HUB</div>
            <div className="text-[8.5px] sm:text-[9px] uppercase tracking-[0.22em] sm:tracking-[0.3em] text-white/55 mt-1.5 font-bold">
              Fleet Operations Platform
            </div>
          </div>
        </div>

        {/* STATUS PILL */}
        <div>
          <div className="inline-flex items-center gap-2 sm:gap-2.5 bg-black/55 backdrop-blur-sm border border-white/12 rounded-full px-3 sm:px-3.5 py-1 sm:py-1.5">
            <span className="relative flex h-2 w-2" aria-hidden>
              <span
                className="absolute inline-flex h-full w-full rounded-full bg-[#34d77a] opacity-70"
                style={{ animation: "zmexStatusPing 2.4s cubic-bezier(0,0,0.2,1) infinite" }}
              />
              <span
                className="relative inline-flex rounded-full h-2 w-2 bg-[#34d77a]"
                style={{ boxShadow: "0 0 8px rgba(52,215,122,0.85), 0 0 18px rgba(52,215,122,0.35)" }}
              />
            </span>
            <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.3em] text-white/90 font-bold whitespace-nowrap">
              A ZMEX Disposal Company
            </span>
          </div>
        </div>
      </header>

      {/* Keyframes injected once — keeps the rotating ring + status ping isolated from global CSS */}
      <style>{`
        @keyframes zmexRingSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes zmexRingIdle { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes zmexStatusPing {
          0%   { transform: scale(1);   opacity: 0.75; }
          70%  { transform: scale(2.2); opacity: 0;    }
          100% { transform: scale(2.2); opacity: 0;    }
        }
      `}</style>

      {/* MAIN GRID — copy left, login card right; dumpster shows through the gap.
          Mobile gets extra top padding to clear the stacked header (logo + badge). */}
      <div className="relative z-10 min-h-screen grid lg:grid-cols-12 items-center px-4 sm:px-10 lg:px-14 pt-40 sm:pt-28 pb-24 gap-6 lg:gap-10">
        {/* LEFT — hero copy. Hidden on phones so the form stays the priority. */}
        <div className="hidden lg:flex flex-col justify-center max-w-xl text-white lg:col-span-4">
          <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-[#CFAB7A] font-bold">
            <span className="h-px w-8 bg-[#CFAB7A]" />
            Fleet Operations Platform
          </div>
          <h1 className="font-heading text-4xl xl:text-5xl 2xl:text-[3.5rem] font-black leading-[0.95] tracking-tight mt-7">
            Built to move <br />
            waste <span className="text-[#CFAB7A]">smarter.</span>
          </h1>
          <p className="mt-7 text-white/70 max-w-md text-base leading-relaxed">
            Precision operations for modern waste hauling companies.
          </p>

          {/* Feature icon row — Routes / Drivers / Service Tracking */}
          <div className="mt-12 flex items-start gap-9 sm:gap-10">
            <FeatureIcon Icon={MapTrifold} label="Route" sub="Management" />
            <FeatureIcon Icon={IdentificationBadge} label="Driver" sub="Operations" />
            <FeatureIcon Icon={ClipboardText} label="Service" sub="Tracking" />
          </div>
        </div>

        {/* SPACER so the dumpster keeps the middle column visually */}
        <div className="hidden lg:block lg:col-span-4" />

        {/* RIGHT — login card */}
        <div className="flex items-center justify-center lg:justify-end lg:col-span-4">
          <form
            onSubmit={onSubmit}
            className="w-full max-w-[420px] rounded-md border border-white/10 bg-[#0a0d12]/85 backdrop-blur-xl shadow-2xl"
            data-testid="login-form"
            style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.75), 0 0 0 1px rgba(207,171,122,0.05)" }}
          >
            <div className="px-6 sm:px-8 pt-7 sm:pt-8 pb-6 sm:pb-7">
              <div className="text-[10px] font-bold uppercase tracking-[0.45em] text-[#CFAB7A]">Sign In</div>
              <h2 className="font-heading text-[1.9rem] sm:text-[2.25rem] font-black text-white mt-2 leading-none">Welcome back</h2>
              <div className="h-[2px] w-12 bg-[#CFAB7A] mt-3" />
              <p className="text-sm text-white/65 mt-4 sm:mt-5">Sign in to access your operations console.</p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/75">Email or Username</label>
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                    placeholder="Enter your email or username"
                    data-testid="login-email"
                    className="mt-2 w-full rounded-sm bg-white/[0.04] border border-white/12 text-white placeholder-white/35 px-3.5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A] focus:border-[#CFAB7A] transition"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/75">Password</label>
                  <div className="relative mt-2">
                    <input
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      data-testid="login-password"
                      className="w-full rounded-sm bg-white/[0.04] border border-white/12 text-white placeholder-white/35 px-3.5 py-3 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A] focus:border-[#CFAB7A] transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? "Hide password" : "Show password"}
                      data-testid="login-password-toggle"
                      className="absolute inset-y-0 right-2 my-auto h-8 w-8 grid place-items-center text-white/55 hover:text-[#CFAB7A] transition"
                    >
                      {showPw ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                    </button>
                  </div>
                </div>

                {err && (
                  <div
                    className="text-sm text-[#FFB4A2] bg-[#D32F2F]/15 border border-[#D32F2F]/35 rounded-sm px-3 py-2"
                    data-testid="login-error"
                  >
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit"
                  className="w-full inline-flex items-center justify-center rounded-sm bg-[#CFAB7A] text-[#012340] font-heading font-black tracking-wide py-3.5 text-base hover:brightness-[1.04] active:brightness-95 disabled:opacity-60 transition-all"
                >
                  <SignIn size={18} weight="bold" className="mr-2" />
                  {busy ? "Signing in…" : "Sign in"}
                </button>
              </div>
            </div>

            <div className="border-t border-white/8 px-6 sm:px-8 py-4">
              <p className="text-[10px] uppercase tracking-[0.32em] text-white/40 font-bold text-center">
                Authorized personnel only
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* FOOTER — copyright (respects iPhone home-indicator safe area) */}
      <div
        className="absolute bottom-0 left-0 right-0 z-10 text-center text-[11px] tracking-wide text-white/40"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)" }}
      >
        © {COPY_YEAR} ZMEX Disposal. All rights reserved.
      </div>
    </div>
  );
}

function FeatureIcon({ Icon, label, sub }) {
  return (
    <div className="flex flex-col items-start gap-2.5">
      <div className="w-11 h-11 grid place-items-center rounded-sm border border-[#CFAB7A]/30 bg-[#CFAB7A]/[0.04] text-[#CFAB7A]">
        <Icon size={20} weight="bold" />
      </div>
      <div className="leading-tight">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white">{label}</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">{sub}</div>
      </div>
    </div>
  );
}
